"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const certifications = [{
  id: "cert-1",
  index: "01 //",
  title: "MSC Certified",
  description: "Our wild-caught seafood is fully traceable to sustainable sources, ensuring the long-term health of our oceans.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721626/img/badge_msc_certified.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721626/img/badge_msc_certified.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721626/img/badge_msc_certified.png-wvc-sizes",
    alt: "MSC Certified Sustainable Seafood Badge"
  },
  link: {
    text: "View Standards",
    url: "/SustainabilityPage"
  }
}, {
  id: "cert-2",
  index: "02 //",
  title: "Gulf Heritage",
  description: "Proudly supporting local fishermen and traditional harvesting practices that have been proven by time.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721626/img/badge_gulf_heritage.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721626/img/badge_gulf_heritage.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721626/img/badge_gulf_heritage.png-wvc-sizes",
    alt: "Gulf Coast Heritage Badge"
  },
  link: {
    text: "Our Story",
    url: "/OurStoryPage"
  }
}, {
  id: "cert-3",
  index: "03 //",
  title: "Quality Assured",
  description: "Expertly cut and delivered with care. Every catch meets our rigorous standards from shore to door.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721626/img/badge_quality_assured.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721626/img/badge_quality_assured.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721626/img/badge_quality_assured.png-wvc-sizes",
    alt: "Premium Quality Assured Badge"
  },
  link: {
    text: "Shop Now",
    url: "/ShopPage"
  }
}];
export default function Certifications() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="certifications-badges" className="bg-background relative w-full overflow-hidden px-8 py-24 md:py-40">
      {}
      <div className="pointer-events-none absolute inset-0 mx-auto flex max-w-[1440px] justify-between px-8 opacity-10">
        <div className="h-full w-[1px] bg-foreground"></div>
        <div className="h-full w-[1px] bg-foreground hidden md:block"></div>
        <div className="h-full w-[1px] bg-foreground hidden md:block"></div>
        <div className="h-full w-[1px] bg-foreground"></div>
      </div>

      <div className="mx-auto max-w-[1440px] relative z-10">
        {}
        <div ref={headerRef} className={`mx-auto mb-16 max-w-3xl text-center transition-all duration-800 ease-out md:mb-24 ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <span className="text-muted-foreground mb-4 block font-mono text-xs font-medium uppercase tracking-[0.25em]">
            Standards & Practices
          </span>
          <h2 className="text-foreground mb-6 font-serif text-4xl font-semibold tracking-tight md:text-5xl lg:text-6xl">
            Proven by Time. Certified by Standards.
          </h2>
          <p className="text-foreground/80 mx-auto max-w-2xl font-sans text-lg leading-relaxed">
            Our commitment to wild-caught practices and ocean health is backed
            by industry-leading certifications. We believe in transparency from
            the moment the catch hits the deck to when it arrives at your door.
          </p>
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {certifications.map((cert, i) => <div key={cert.id} data-index={i} className={`transition-all duration-800 ease-out`} style={{
          transitionDelay: `${i * 150}ms`,
          opacity: isGridVisible ? 1 : 0,
          transform: isGridVisible ? "translateY(0)" : "translateY(30px)"
        }}>
              <Card className="border-border bg-card group flex h-full flex-col items-center rounded-md border shadow-sm transition-colors duration-300 hover:border-primary">
                <CardHeader className="flex flex-col items-center pb-4 pt-10 text-center">
                  <div className="mb-6 flex h-32 w-32 items-center justify-center overflow-hidden rounded-lg border border-border bg-background p-4 shadow-sm transition-transform duration-500 group-hover:scale-105">
                    <img src={cert.image.src} data-wvc-srcset={cert.image.srcset} data-wvc-sizes={cert.image.sizes} alt={cert.image.alt} loading="lazy" className="h-full w-full object-contain opacity-90" />
                  </div>
                  <CardTitle className="text-foreground font-serif text-2xl font-semibold">
                    <span className="text-primary mr-2 font-mono text-sm font-normal tracking-widest">
                      {cert.index}
                    </span>
                    {cert.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow text-center">
                  <CardDescription className="text-foreground/70 font-sans text-base leading-relaxed">
                    {cert.description}
                  </CardDescription>
                </CardContent>
                <CardFooter className="pb-10 pt-4">
                  <Button asChild variant="outline" className="border-foreground text-foreground hover:bg-foreground hover:text-background rounded-md font-mono text-xs uppercase tracking-widest transition-colors duration-300">
                    <Link to={cert.link.url}>{cert.link.text}</Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}